import {
  TextField,
  PasswordField,
  FormField,
  TextFieldList,
  formFieldRequiredLabel as requiredLabel,
  InfoTooltip,
  Flex,
} from '@elementary/common-ui/components';
import { Controller, UseFormReturn } from 'react-hook-form';
import { LightdashForm } from 'shared/types';

type Props = {
  formMethods: UseFormReturn<LightdashForm>;
};

const LightdashDialogForm = ({ formMethods }: Props) => {
  const {
    register,
    formState: { errors, dirtyFields },
    setValue,
    control,
  } = formMethods;

  return (
    <>
      <FormField
        title="Connection name"
        errorMessage={errors.lightdashConnectionName?.message as string}
      >
        <TextField
          {...register('lightdashConnectionName', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="My Lightdash"
          inputProps={{ maxLength: 50 }}
          size="large"
        />
      </FormField>

      <FormField
        title="API token"
        errorMessage={errors.lightdashApiToken?.message as string}
      >
        <PasswordField
          {...register('lightdashApiToken', {
            required: requiredLabel,
          })}
          placeholder="ldpat_1234..."
          isFormValueDirty={dirtyFields.lightdashApiToken}
          clearFormValue={() => setValue('lightdashApiToken', '')}
          size="large"
        />
      </FormField>

      <FormField
        title={
          <Flex gap={1} alignItems="center">
            Host URL
            <InfoTooltip
              size={16}
              title="Leave empty to use the default Lightdash cloud URL (https://app.lightdash.cloud). Set this only for self-hosted instances."
            />
          </Flex>
        }
        errorMessage={errors.lightdashHostUrl?.message as string}
        optional
      >
        <TextField
          {...register('lightdashHostUrl', {
            pattern: {
              value: /^(https?:\/\/.+)?$/,
              message: 'Must be a valid URL starting with http:// or https://',
            },
          })}
          variant="outlined"
          placeholder="https://app.lightdash.cloud"
          size="large"
        />
      </FormField>

      <FormField
        title={
          <Flex gap={1} alignItems="center">
            Project UUIDs
            <InfoTooltip
              size={16}
              title="Leave empty to sync all projects in your organization. Add specific UUIDs to sync only those projects."
            />
          </Flex>
        }
        optional
      >
        <Controller
          name="lightdashProjectUuids"
          control={control}
          render={({ field }) => (
            <TextFieldList
              {...field}
              sx={{ maxHeight: '300px', overflow: 'auto' }}
              buttonText="Add project UUID"
              placeholder="project UUID"
            />
          )}
        />
      </FormField>
    </>
  );
};

export default LightdashDialogForm;
